import { Device } from "@capacitor/device";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import InfoModal from "../../components/layouts/InfoModal";
import ToastDefault from "../../components/layouts/Toast";
import { Routes } from "../../constants/RouteConstants";
import AboutUsPublic from "../cms-public/AboutUs";
import ContactUsPublic from "../cms-public/ContactUs";
import FaqsPublic from "../cms-public/FaqsPublic";
import PrivacyPolicyPublic from "../cms-public/PrivacyPolicy";
import TermsAndConditionsPublic from "../cms-public/TermsAndConditions";
import "./Home.css";

const Home = () => {
  const navigate = useNavigate();
  // const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalTitle, setModalTitle] = useState("");
  const [imageOrder, setImageOrder] = useState([0, 1, 2]);
  const [movingBackIdx, setMovingBackIdx] = useState<number | null>(null);

  const handleImageClick = () => {
    if (movingBackIdx !== null) return;
    const currentFrontIdx = imageOrder.findIndex((pos) => pos === 2);
    setMovingBackIdx(currentFrontIdx);
    setImageOrder((prev) => prev.map((pos) => (pos + 1) % 3));

    setTimeout(() => {
      setMovingBackIdx(null);
    }, 500); // Match animation duration
  };


  const openModal = (title: string, content: React.ReactNode) => {
    setModalTitle(title);
    setModalContent(content);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalContent(null);
    setModalTitle("");
  };

  useEffect(() => {
    const check = async () => {
      const { platform } = await Device.getInfo();
      if (platform !== "web") {
        navigate(Routes?.Login);
      }
    };
    check();
  }, []);

  const toggleMenu = () => {
    setMenuOpen((prev) => {
      const isOpening = !prev;
      document.body.classList.toggle("menu-open", isOpening);
      return isOpening;
    });
  };

  return (
    <div className="safe-area-container">
      {/* {loading ? <PageLoader /> : ""} */}
      <InfoModal isOpen={modalOpen} onClose={closeModal} title={modalTitle}>
        {modalContent}
      </InfoModal>
      <ToastDefault />
      <div className="home-no-testimonials animate__animated animate__fadeInUp">
        {/* Hero Section */}
        <div className="hero-section animate__animated animate__fadeInUp ">
          <div className="hero-background"></div>

          {/* Header & Navigation */}
          <header role="banner" className="hero-header">
            <nav className="navigation" aria-label="Main Navigation">
              <img
                className="logo"
                alt="Logo"
                src="./assets/images/landingV2/logo.webp"
                loading="lazy"
              />

              <div
                className={`menu-icon ${menuOpen ? "active" : ""}`}
                onClick={toggleMenu}
              >
                <img
                  src="./assets/images/landingV2/humbarger.png"
                  height={48}
                  width={48}
                  alt=""
                  aria-hidden="true"
                />
              </div>

              <div className={`nav-buttons  ${menuOpen ? "show" : ""}`}>
                <button className="btn btn-primary-second desktop-hide">
                  Download The App
                </button>
                <Link to={Routes?.Login} className="btn btn-secondary">
                  Sign In
                </Link>
              </div>
            </nav>
          </header>

          {/* Hero Content */}
          <section role="region" aria-label="Introduction" className="hero-content animate__animated animate__zoomIn">
            <h1 className="hero-title">
              Where fairer <br /> feels freer.
            </h1>

            {/* Group description and buttons so they can be positioned together on mobile */}
            <div className="hero-footer">
              <p className="hero-description">
                It’s time to be recognized, valued, and rewarded. Turn invisible
                labor into shared responsibility with Justivide.
              </p>
              <div className="hero-buttons">
                <button className="btn btn-large btn-primary">
                  Download the App
                </button>
                <Link
                  to={Routes?.SignUp}
                  className="btn btn-large btn-tertiary"
                >
                  Try for Free
                </Link>
              </div>
            </div>
          </section>
        </div>

        {/* Main Content Landmark */}
        <main id="main-content">

        {/* Mission Section */}
        <section
          className="mission-section wow animate__fadeInUp"
          data-wow-duration="0.5s"
        >
          <div className="mission-container">
            <h2 className="section-title">
              We believe in equality and in the dignity of all work.
            </h2>
            <p className="section-description">
              Justivide is the first app to recognize, reward, and rebalance
              invisible household labor — from daily chores to the emotional and
              mental load — in a tangible way. We assign real financial value to
              contributions, so every member of a household can share the work
              fairly — and see it pay off.
              <br />
              <br />
              No point systems. No guilt. Just fairness, freedom, and equality
              that actually works.
            </p>
            <Link
              to={Routes?.SignUp}
              className="btn btn-primary btn-center"
              style={{ width: "fit-content" }}
            >
              See How It Works
            </Link>
          </div>
          <img
            className="mission-mockup"
            alt="Phone mockup"
            src="./assets/images/landingV2/PhoneNew.webp"
            loading="eager"
          />
        </section>

        <section className="features-section">
          <div className="features-background"></div>
          <div className="container">
            <h2 className="section-title dark">
              A better way to share the load.
            </h2>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-image">
                  <img
                    alt="Track Chores"
                    src="./assets/images/landingV2/frame1.png"
                  />
                </div>
                <div className="feature-content">
                  <h3 className="feature-title">Track Chores</h3>
                  <p className="feature-description">
                    Assign visible + invisible labor — from dishes to planning
                    appointments.
                  </p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-image">
                  <img
                    alt="Adjust Contributions"
                    src="./assets/images/landingV2/frame2.png"
                  />
                </div>
                <div className="feature-content">
                  <h3 className="feature-title">Adjust Contributions</h3>
                  <p className="feature-description">
                    Automatically adjust how shared expenses are divided based
                    on household labor.
                  </p>
                </div>
              </div>

              <div className="feature-card">
                <div className="feature-image">
                  <img
                    alt="Get Rewarded"
                    src="./assets/images/landingV2/frame3.png"
                  />
                </div>
                <div className="feature-content">
                  <h3 className="feature-title">Get Rewarded</h3>
                  <p className="feature-description">
                    Real financial recognition — because emotional labor
                    deserves more than just "thank you."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="stats-section">
          <div className="container">
            <div className="stats-content">
              <div className="stat-card">
                <div className="stat-number">77%</div>
                <p className="stat-text">
                  of adults believe household labor should be shared¹ — but one
                  person still ends up carrying most of the load. That imbalance
                  isn't just exhausting, it's invisible.
                  <br />
                  <br />
                  <a
                    href="https://thegepi.org/the-free-time-gender-gap/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="stat-link"
                  >
                    ¹GEPI (Gender Equity Policy Institute)
                  </a>
                </p>
              </div>
              <h2 className="stats-title">
                Your work is real. We make the value real too.
              </h2>
            </div>

            <div className="stats-benefits">
              <p className="benefits-intro">
                We're changing that. Justivide transforms emotional, mental, and
                physical labor into recognized, rewarded work. The result?
              </p>
              <div className="benefits-list">
                <div className="benefit-item">
                  <img src="./assets/images/landingV2/check.png" alt="Check" />
                  <span>Less stress.</span>
                </div>
                <div className="benefit-item">
                  <img src="./assets/images/landingV2/check.png" alt="Check" />
                  <span>Stronger relationships.</span>
                </div>
                <div className="benefit-item">
                  <img src="./assets/images/landingV2/check.png" alt="Check" />
                  <span>A more equal home.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Target Audience Section */}
        <section className="audience-section">
          <div className="audience-background"></div>
          <div className="container">
            <h2 className="section-title">
              Designed for fairness. Built for every household.
            </h2>

            <div className="audience-content">
              <div className="audience-cards">
                <div className="audience-card purple">
                  <div className="audience-card-content">
                    <h3 className="audience-card-title">The Default Doer</h3>
                    <p className="audience-card-text">
                      You're tired of reminding, planning, and picking up the
                      slack. You want a system that values your time — and makes
                      equality possible.
                    </p>
                  </div>
                  <img
                    src="./assets/images/landingV2/yelloArrow.png"
                    className="audience-icon"
                  />
                </div>

                <div className="audience-card yellow">
                  <div className="audience-card-content">
                    <h3 className="audience-card-title dark">
                      The (Almost) Equal Partner
                    </h3>
                    <p className="audience-card-text dark">
                      You value fairness — but didn't realize how much you
                      weren't doing. You want a way to help, without being told
                      every time.
                    </p>
                  </div>
                  <img
                    src="./assets/images/landingV2/blackArrow.png"
                    className="audience-icon"
                  />
                </div>

                <div className="audience-card green">
                  <div className="audience-card-content">
                    <h3 className="audience-card-title">Roommates & Couples</h3>
                    <p className="audience-card-text">
                      You split rent — but what about trash, dishes, or
                      cleaning? Justivide makes it easy to track and split all
                      the work, fairly.
                    </p>
                  </div>
                  <img
                    src="./assets/images/landingV2/whiteArrow.png"
                    className="audience-icon"
                  />
                </div>
              </div>

              <div className="audience-image">
                <img
                  alt="Group of people"
                  src="./assets/images/landingV2/group.png"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section">
          <div className="container">
            <h2 className="section-title dark">Simple steps. Real results.</h2>

            <div className="steps-wrapper">
              <div className="vertical-line"></div>

              <div className="steps-list">
                <div className="step-row">
                  <div className="step-number">1</div>

                  <div className="step-item">
                    <div className="step-icon">
                      <img
                        src="./assets/images/landingV2/step1.png"
                        alt="Step 1"
                      />
                    </div>
                    <div className="step-content">
                      <h3 className="step-title">Track your household labor</h3>
                      <p className="step-description">
                        Chores, planning, care work — you decide what matters.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="step-row">
                  <div className="step-number">2</div>

                  <div className="step-item">
                    <div className="step-icon">
                      <img
                        src="./assets/images/landingV2/step2.png"
                        alt="Step 2"
                      />
                    </div>
                    <div className="step-content">
                      <h3 className="step-title">Log shared expenses</h3>
                      <p className="step-description">
                        Easily track your expenses to ensure fairness across all
                        categories.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="step-row">
                  <div className="step-number">3</div>

                  <div className="step-item">
                    <div className="step-icon">
                      <img
                        src="./assets/images/landingV2/step3.png"
                        alt="Step 3"
                      />
                    </div>
                    <div className="step-content">
                      <h3 className="step-title">
                        See contributions calculated
                      </h3>
                      <p className="step-description">
                        The app shows who's doing what — clearly and fairly.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="step-row">
                  <div className="step-number">4</div>

                  <div className="step-item">
                    <div className="step-icon">
                      <img
                        src="./assets/images/landingV2/step4.png"
                        alt="Step 4"
                      />
                    </div>
                    <div className="step-content">
                      <h3 className="step-title">Rebalance shared expenses</h3>
                      <p className="step-description">
                        Contributions adjust automatically to reflect each
                        person's work.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* App Features Section */}
        <section className="app-features-section">
          <div className="container">
            <div className="app-features-header">
              <h2 className="section-title dark">Find your fair.</h2>
              <p className="section-subtitle dark">
                Because advancing equality starts at home.
              </p>
            </div>

            <div className="app-screens" onClick={handleImageClick}>
              <img
                src="./assets/images/landingV2/findImage4.png"
                alt="Dashboard"
                loading="lazy"
                className={`screen-img pos-${imageOrder[0]} ${movingBackIdx === 0 ? "moving-back" : ""
                  }`}
              />
              <img
                src="./assets/images/landingV2/findImage5.png"
                alt="Dashboard"
                loading="lazy"
                className={`screen-img pos-${imageOrder[1]} ${movingBackIdx === 1 ? "moving-back" : ""
                  }`}
              />
              <img
                src="./assets/images/landingV2/Img3.webp"
                alt="Dashboard"
                loading="lazy"
                className={`screen-img pos-${imageOrder[2]} ${movingBackIdx === 2 ? "moving-back" : ""
                  }`}
              />
            </div>

            <div className="app-features-grid">
              <div className="app-feature">
                <h3 className="app-feature-title">
                  Explore 5 ways to split<br />expenses
                </h3>
                <p className="app-feature-text">
                  Share the bills your way—by income, by time, or a little
                  mix-and-match magic!
                </p>
              </div>
              <div className="app-feature">
                <h3 className="app-feature-title">Opportunity-cost<br />pricing</h3>
                <p className="app-feature-text">
                  Claim credit for the opportunities you missed from promotions
                  to experiences.
                </p>
              </div>
              <div className="app-feature">
                <h3 className="app-feature-title">
                  Task-based vs. task-neutral pricing
                </h3>
                <p className="app-feature-text">
                  Value every task equally or match set rates.
                </p>
              </div>
              <div className="app-feature">
                <h3 className="app-feature-title">Payment tracking</h3>
                <p className="app-feature-text">
                  Easily share payment methods and stay on top of balances from month to month.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="testimonials-section">
          <div className="container">
            <div className="app-features-header">
              <h6 className="section-title dark" style={{ fontSize: "" }}>
                Life's smoother when the load is shared.
              </h6>
            </div>
          </div>
          <div className="test-image">
            <img
              alt="Group of people"
              src="./assets/images/landingV2/image.png"
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-card">
            <div className="cta-content">
              <h2 className="cta-title">Let's work together, evenly.</h2>
              <p className="cta-description">
                You're not imagining it — you're just doing more. It's time to
                get fair. Join the households already building more balanced
                partnerships with Justivide.
              </p>
              <div className="cta-buttons">
                <button
                  className="btn btn-large btn-primary-second"
                  style={{ color: "white" }}
                >
                  Download the App
                </button>
                <Link
                  to={Routes?.SignUp}
                  className="btn btn-large btn-secondary-light"
                >
                  Get Started
                </Link>
              </div>
              <p className="cta-note">
                Justivide works for couples, co-parents, and roommates. All
                contributions count.
              </p>
            </div>
            <img
              className="cta-mockup"
              alt="Phone mockup"
              src="./assets/images/landingV2/PhoneNew2.webp"
              loading="lazy"
            />
          </div>
        </section>
        </main>

        {/* Footer */}
        <footer role="contentinfo" className="footer">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <img
                  className="footer-logo"
                  alt="Logo"
                  src="./assets/images/landingV2/logo.webp"
                  loading="lazy"
                />
                <p className="footer-tagline">Find your fair.</p>
              </div>

              <div className="footer-links">
                <div className="footer-column">
                  <button
                    className="footer-link-title btn-link"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                    }}
                    onClick={() =>
                      openModal(
                        "Contact Us",
                        <ContactUsPublic onSuccess={closeModal} />,
                      )
                    }
                  >
                    Contact Us
                  </button>
                  <button
                    className="footer-link btn-link"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                    }}
                    onClick={() =>
                      openModal(
                        "Terms & Conditions",
                        <TermsAndConditionsPublic />,
                      )
                    }
                  >
                    Terms & Conditions
                  </button>
                  <button
                    className="footer-link btn-link"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                    }}
                    onClick={() =>
                      openModal("Privacy Policy", <PrivacyPolicyPublic />)
                    }
                  >
                    Privacy Policy
                  </button>
                </div>

                <div className="footer-column">
                  <button
                    className="footer-link-title btn-link"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                    }}
                    onClick={() => openModal("About Us", <AboutUsPublic />)}
                  >
                    About Us
                  </button>
                  <button
                    className="footer-link btn-link"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                    }}
                    onClick={() => openModal("FAQs", <FaqsPublic />)}
                  >
                    FAQs
                  </button>
                </div>

                <div className="footer-column">
                  <img
                    className="footer-qr"
                    alt="QR Code"
                    src="./assets/images/landingV2/footerLogo.png"
                  />
                </div>
              </div>
            </div>

            <div className="footer-divider"></div>

            <p className="footer-copyright">
              Copyright © 2026 Justivide. All Rights Reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Home;
